<template>
    <div
        v-if="hasTickets && betSlipState === BetSlipState.Medium"
        class="single-cont"
        data-bet-slip-scroll-boundary
    >
        <section
            ref="externalScrollAreaElement"
            class="single betSlip _singleTicket _singleMode notranslate"
            v-bind:class="{
                _stakePadExpanded: shouldExpandBetSlip,
                _parlayTab: isParlayTabActive,
            }"
        >
            <header class="single-header">
                <div class="single-headerInfo">
                    <span class="single-ticketCount" data-testid="ticket-count">
                        {{ headerTicketCountDisplay }}
                    </span>
                    <span class="single-title" data-testid="single-title">
                        {{ headerTitleDisplay }}
                    </span>
                    <span
                        v-if="headerOddsDisplay"
                        class="single-odds notranslate"
                        data-testid="single-total-odds"
                    >@{{ headerOddsDisplay }}</span>
                    <button
                        v-if="isTutorialEntryVisible"
                        class="single-questionButton BetSlips_Btn_TutorialTip"
                        type="button"
                        aria-label="Open tutorial"
                        data-testid="single-tutorial-entry"
                        v-on:click="openTutorialFromBetSlip"
                    >
                        <SboIcon
                            name="system:questionOutline"
                            size="20"
                        />
                    </button>
                </div>
                <div class="single-actions">
                    <button
                        class="single-clear"
                        v-bind:class="`${gaClassPrefix}_Btn_RemoveAll`"
                        data-testid="clear-all-single"
                        type="button"
                        aria-label="Clear bet slip"
                        v-bind:disabled="isBetSlipInteractionLocked"
                        v-on:click="removeAllTickets"
                    >
                        <SboIcon name="system:trashOutline" size="20" />
                    </button>
                    <button
                        class="single-expand"
                        v-bind:class="`${gaClassPrefix}_Btn_Expand`"
                        data-testid="expand-single"
                        type="button"
                        aria-label="Expand bet slip"
                        v-bind:disabled="isBetSlipInteractionLocked"
                        v-on:click="expandBetSlip"
                    >
                        <SboIcon name="system:expand" size="20" />
                    </button>
                </div>
                <button
                    class="single-collapse"
                    v-bind:class="`${gaClassPrefix}_Btn_Collapse`"
                    data-testid="collapse-single"
                    type="button"
                    aria-label="Collapse"
                    v-bind:disabled="isBetSlipInteractionLocked"
                    v-on:click="collapseBetSlip"
                >
                    <SboIcon name="system:arrowDown" size="20" />
                </button>
            </header>
            <BetSlipTabs
                v-bind:activeTab="activeTab"
                v-bind:gaSizePrefix="getBetSlipGaSizePrefix('Md')"
                v-bind:isDisabled="isBetSlipInteractionLocked"
                v-on:switchTab="switchTab"
            />
            <MultipleSingleBetSlip
                v-if="!isParlayTabActive"
                ref="ticketListComponent"
                v-bind:gaClassPrefix="gaClassPrefix"
                v-bind:hasFooterError="footerErrorMessage !== null"
            />
            <ParlayInSingleBetSlip
                v-else
                ref="ticketListComponent"
                v-bind:gaClassPrefix="gaClassPrefix"
                v-on:addMoreBet="collapseBetSlipForAddMoreBet"
            />
            <footer class="single-footer">
                <div v-if="!isParlayTabActive" class="single-footerCard">
                    <Message
                        v-if="footerErrorMessage"
                        class="single-footerMessage"
                        status="error"
                        v-bind:messageKey="footerErrorMessage.messageKey"
                        v-bind:messageData="footerErrorMessage.messageData"
                        data-testid="single-footer-error"
                    />
                    <SwitchVoucherRedeem
                        v-if="isShowVoucherSwitch"
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:isRedeemVoucher="isVoucherRedemptionEnabled"
                        v-bind:disabled="isBetSlipInteractionLocked"
                        v-on:toggleRedeemVoucher="switchRedeemVoucherMode"
                    />
                    <BetSlipCta
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:ctaAction="renderedCtaAction"
                        v-bind:ticketCount="ctaTicketCount"
                        v-bind:disabled="isRenderedCtaDisabled"
                        v-bind:loading="isPlacingBet"
                        v-on:click="handleRenderedCtaClick"
                    />
                    <div class="single-summary">
                        <span data-testid="total-stake">
                            {{ i18n.t('bet_slip_total_stake') }}
                            <span class="single-summaryAmount">{{ totalStakeDisplay }}</span>
                        </span>
                        <span data-testid="total-max-payout">
                            {{ i18n.t('bet_slip_total_max_payout') }}
                            <span class="single-summaryAmount">{{ totalMaxPayoutDisplay }}</span>
                        </span>
                    </div>
                </div>
                <div v-else class="parlay-footerCard">
                    <Message
                        v-if="parlayFooterErrorMessage"
                        class="single-footerMessage"
                        status="error"
                        v-bind:messageKey="parlayFooterErrorMessage.messageKey"
                        v-bind:messageData="parlayFooterErrorMessage.messageData"
                        data-testid="parlay-footer-error"
                    />
                    <div class="parlay-actions">
                        <StakeBox
                            v-bind:gaClassPrefix="gaClassPrefix"
                            v-bind:disabled="isBetSlipInteractionLocked"
                            v-bind:stake="parlayStake"
                            v-bind:minBet="parlayMinBet"
                            v-bind:maxBet="parlayMaxBet"
                            v-bind:isInvalid="parlayStakeError !== null"
                            v-bind:isActive="isParlayStakePadOpen"
                            v-on:activate="openParlayStakePad"
                            v-on:updateStake="setParlayStake"
                        />
                        <BetSlipCta
                            class="parlay-placeBet"
                            v-bind:gaClassPrefix="gaClassPrefix"
                            v-bind:ctaAction="renderedParlayCtaAction"
                            v-bind:ticketCount="parlayCtaTicketCount"
                            v-bind:disabled="isRenderedParlayCtaDisabled"
                            v-bind:loading="isPlacingParlay"
                            v-on:click="handleRenderedParlayCtaClick"
                        />
                    </div>
                    <div class="parlay-payout">
                        <span>{{ i18n.t('bet_slip_total_max_payout') }}</span>
                        <span class="notranslate parlay-payoutAmount" data-testid="parlay-max-payout">
                            {{ parlayMaxPayoutDisplay }}
                        </span>
                    </div>
                    <StakePad
                        v-if="isParlayStakePadOpen"
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-on:appendDigits="appendParlayStakeDigits"
                        v-on:addAmount="addParlayStakeAmount"
                        v-on:max="setMaxParlayStake"
                        v-on:backspace="backspaceParlayStake"
                        v-on:close="closeParlayStakePad"
                    />
                </div>
                <SwitchAcceptAnyOddsValue
                    v-if="!customerInfo.isAcceptAnyOdds"
                    class="single-acceptAnyOdds"
                    v-bind:gaClassPrefix="gaClassPrefix"
                />
            </footer>
        </section>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import type { ComponentPublicInstance } from 'vue';
    import {
        computed,
        onUnmounted,
        ref,
    } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import BetSlipCta from '@/components/betSlip/Single/BetSlipCta.vue';
    import BetSlipTabs from '@/components/betSlip/Single/BetSlipTabs.vue';
    import MultipleSingleBetSlip from '@/components/betSlip/Single/MultipleSingleBetSlip.vue';
    import ParlayInSingleBetSlip from '@/components/betSlip/Single/ParlayInSingleBetSlip.vue';
    import StakeBox from '@/components/betSlip/Single/StakeBox.vue';
    import StakePad from '@/components/betSlip/Single/StakePad.vue';
    import Message from '@/components/common/Message.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import SwitchVoucherRedeem from '@/components/voucher/SwitchVoucherRedeem.vue';
    import {
        BetSlipState,
        RoutePage,
        SingleModeBetSlipTab,
    } from '@/interface/enum';
    import { getBetSlipGaClassPrefix, getBetSlipGaSizePrefix } from '@/libraries/betSlip/betSlipGaClass';
    import { useSingleModeBetSlipTutorialState } from '@/libraries/composables/popup/useSingleModeBetSlipTutorialState';
    import { useBindExternalScrollArea } from '@/libraries/composables/useBindExternalScrollArea';
    import { useParlayInSingleCta } from '@/libraries/composables/useParlayInSingleCta';
    import { useSingleModeBetSlipCta } from '@/libraries/composables/useSingleModeBetSlipCta';
    import { i18n } from '@/libraries/i18n';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    const { customerInfo } = storeToRefs(useAccountStore());
    const { isTutorialEntryVisible, openTutorialFromBetSlip } = useSingleModeBetSlipTutorialState();

    const { betSlipState } = storeToRefs(useBetSlipStore());
    const { showBigBetSlip, showMiniBetSlip } = useBetSlipStore();

    const {
        activeTab,
        hasTickets,
        isStakePadOpen,
        isPlacingBet,
        isBetSlipInteractionLocked,
        isVoucherRedemptionEnabled,
        isShowVoucherSwitch,
        totalStakeDisplay,
        totalMaxPayoutDisplay,
        betSlipTitle,
        shouldShowParlaySelectionWarning,
        headerTicketCount,
        headerOddsDisplay,
        parlayStake,
        parlayStakeError,
        parlayMinBet,
        parlayMaxBet,
        parlayMaxPayoutDisplay,
        isPlacingParlay,
        isParlayStakePadOpen,
    } = storeToRefs(useSingleModeBetSlipStore());

    const {
        closeStakePad,
        removeAllTickets,
        switchRedeemVoucherMode,
        switchTab,
        setParlayStake,
        appendParlayStakeDigits,
        addParlayStakeAmount,
        setMaxParlayStake,
        backspaceParlayStake,
        openParlayStakePad,
        closeParlayStakePad,
    } = useSingleModeBetSlipStore();
    const {
        footerErrorMessage,
        ctaTicketCount,
        renderedCtaAction,
        isRenderedCtaDisabled,
        handleRenderedCtaClick,
    } = useSingleModeBetSlipCta();
    const {
        renderedParlayCtaAction,
        parlayCtaTicketCount,
        isRenderedParlayCtaDisabled,
        parlayFooterErrorMessage,
        handleRenderedParlayCtaClick,
    } = useParlayInSingleCta();

    const gaClassPrefix = computed(() => getBetSlipGaClassPrefix('Md', activeTab.value));
    const isParlayTabActive = computed(() => activeTab.value === SingleModeBetSlipTab.Parlay);
    const shouldExpandBetSlip = computed(() => (
        isParlayTabActive.value
            ? isParlayStakePadOpen.value || parlayFooterErrorMessage.value !== null
            : isStakePadOpen.value && !isVoucherRedemptionEnabled.value
    ));
    const headerTicketCountDisplay = computed(() => (
        shouldShowParlaySelectionWarning.value ? '!' : headerTicketCount.value
    ));
    const headerTitleDisplay = computed(() => (
        isParlayTabActive.value && parlayFooterErrorMessage.value !== null
            ? i18n.t('bottom_function_bet_slip')
            : betSlipTitle.value
    ));

    const externalScrollAreaElement = ref<HTMLElement | null>(null);
    const ticketListComponent = ref<ComponentPublicInstance | null>(null);
    const route = useRoute();
    const router = useRouter();
    useBindExternalScrollArea(externalScrollAreaElement, ticketListComponent);

    function collapseBetSlip() {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        closeStakePad();
        showMiniBetSlip();
    }

    function collapseBetSlipForAddMoreBet() {
        collapseBetSlip();
        if (route.name !== RoutePage.Main && route.name !== RoutePage.MainBySport) {
            router.push({ name: RoutePage.Main });
        }
    }

    function expandBetSlip() {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        closeStakePad();
        showBigBetSlip();
    }

    onUnmounted(() => {
        closeParlayStakePad();
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/betSlip";
@import "@/scss/components/singleMode/single";
</style>
